import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  List,
  ListItem,
  Skeleton,
} from '@mui/material';
import {
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import CollapsibleCard from '../common/CollapsibleCard';
import { useAppSelector, useAppDispatch } from '../../store';
import { toggleLatestRecommendation } from '../../store/slices/uiSlice';
import { setSelectedId } from '../../store/slices/scenariosSlice';
import { loadHistoricalResult } from '../../store/slices/analysisSlice';
import { showSnackbar } from '../../store/slices/uiSlice';
import { apiService } from '../../services/api';
import { AllocationResult, ApprovalStatus } from '../../types';


const STATUS_CONFIG: Record<ApprovalStatus, { color: string; bg: string; border: string; label: string }> = {
  proposed: {
    color: '#1976d2',
    bg: '#e3f2fd',
    border: '#90caf9',
    label: 'PROPOSED',
  },
  approved: {
    color: '#388e3c',
    bg: '#e8f5e9',
    border: '#a5d6a7',
    label: 'APPROVED',
  },
  rejected: {
    color: '#d32f2f',
    bg: '#ffebee',
    border: '#ef9a9a',
    label: 'REJECTED',
  },
};


const LatestRecommendationCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector((state) => state.ui.latestRecommendationExpanded);
  const { latestAllocation, latestAllocationLoading, latestAllocationError } = useAppSelector(
    (state) => state.scenarios
  );

  const handleLoadRun = async () => {
    const runId = latestAllocation?.run_id;
    if (!runId) return;

    try {
      const loaded = await apiService.getAllocation(runId);
      if (!loaded) {
        dispatch(showSnackbar({ message: 'Selected run was not found.', severity: 'error' }));
        return;
      }

      const hydrated: AllocationResult = {
        run_id: loaded.run_id || '',
        scenario_id: loaded.scenario_id || '',
        status: loaded.status === 'failed' ? 'failed' : 'success',
        approval_status: loaded.approval_status || 'proposed',
        allocation_plan: loaded.allocation_plan,
        multi_allocation_plan: loaded.multi_allocation_plan,
        deficit_allocation_plan: loaded.deficit_allocation_plan,
        mixed_allocation_plan: loaded.mixed_allocation_plan,
        reconciliation_result: loaded.reconciliation_result,
        reconciliation_scenario: loaded.reconciliation_scenario,
        executive_summary: loaded.executive_summary,
        detailed_reasoning: loaded.detailed_reasoning,
        risk_factors: loaded.risk_factors,
        excluded_region_explanations: loaded.excluded_region_explanations,
        error: loaded.error,
        weights: loaded.weights,
        user_modifications: loaded.user_modifications,
        stored_at: loaded.stored_at,
        plan_type: loaded.plan_type,
        comparison_stats: loaded.comparison_stats,
        business_comparison: loaded.business_comparison,
        region_contexts: loaded.region_contexts,
        selected_strategy: loaded.selected_strategy,
        normalization_note: loaded.normalization_note,
        comparison_filters: loaded.comparison_filters,
        chat_brief: loaded.chat_brief,
        suggested_questions: loaded.suggested_questions,
      };

      dispatch(setSelectedId(hydrated.scenario_id));
      dispatch(loadHistoricalResult(hydrated));
      dispatch(showSnackbar({ message: `Loaded run ${runId.slice(0, 8)}.`, severity: 'success' }));
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to load selected run.', severity: 'error' }));
    }
  };


  if (latestAllocationLoading) {
    return (
      <CollapsibleCard
        title="Latest Recommendation"
        icon={<HistoryIcon sx={{ fontSize: 18 }} />}
        expanded={expanded}
        onToggle={() => dispatch(toggleLatestRecommendation())}
      >
        <Box>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" height={80} sx={{ mt: 2, borderRadius: 1 }} />
        </Box>
      </CollapsibleCard>
    );
  }

  if (latestAllocationError || !latestAllocation) {
    return (
      <CollapsibleCard
        title="Latest Recommendation"
        icon={<HistoryIcon sx={{ fontSize: 18 }} />}
        expanded={expanded}
        onToggle={() => dispatch(toggleLatestRecommendation())}
      >
        <Typography variant="body2" color="text.secondary">
          {latestAllocationError || 'No recommendations available yet.'}
        </Typography>
      </CollapsibleCard>
    );
  }

  const status = latestAllocation.approval_status || 'proposed';
  const statusConfig = STATUS_CONFIG[status];
  const runId = latestAllocation.run_id?.slice(0, 8) || 'N/A';

  const deficitPlan = latestAllocation.deficit_allocation_plan;
  const mixedPlan = latestAllocation.mixed_allocation_plan;
  const allocationPlan = latestAllocation.allocation_plan || latestAllocation.multi_allocation_plan;

  let totalSupply = 0;
  let totalDemand = 0;
  let fulfillmentRate = 0;
  let shortfall = 0;

  if (deficitPlan) {
    totalSupply = deficitPlan.total_supply || 0;
    totalDemand = deficitPlan.total_demand || 0;
    fulfillmentRate = deficitPlan.fulfillment_rate || 0;
    shortfall = deficitPlan.shortfall || 0;
  } else if (mixedPlan?.deficit_plans?.[0]) {
    const dp = mixedPlan.deficit_plans[0];
    totalSupply = dp.total_supply || 0;
    totalDemand = dp.total_demand || 0;
    fulfillmentRate = dp.fulfillment_rate || 0;
    shortfall = dp.shortfall || 0;
  }

  const executiveSummary = latestAllocation.executive_summary || '';
  const storedAt = latestAllocation.stored_at
    ? new Date(latestAllocation.stored_at).toISOString().replace('T', 'T').slice(0, 19) + ' UTC'
    : 'N/A';

  return (
    <CollapsibleCard
      title="Latest Recommendation"
      icon={<HistoryIcon sx={{ fontSize: 18 }} />}
      expanded={expanded}
      onToggle={() => dispatch(toggleLatestRecommendation())}
    >
      <Typography
        sx={{
          fontSize: '11px',
          color: '#9e9e9e',
          marginBottom: '8px',
        }}
      >
        Pulled from saved agent runs. Approved recommendations are shown first, then latest run fallback.
      </Typography>

      {/* Scenario Title */}
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#1a1a2e',
          marginBottom: '6px',
        }}
      >
        {latestAllocation.scenario_title || latestAllocation.scenario_id || 'Unknown Scenario'}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: '10px' }}>
        <Chip
          label={statusConfig.label}
          size="small"
          sx={{
            backgroundColor: statusConfig.bg,
            color: statusConfig.color,
            border: `1px solid ${statusConfig.border}`,
            fontSize: '10px',
            fontWeight: 600,
            height: '22px',
          }}
        />
        <Typography sx={{ color: '#9e9e9e', fontSize: '11px' }}>
          · Run ID:{' '}
          <Box
            component="span"
            sx={{
              color: 'primary.main',
              backgroundColor: '#ede7f6',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: "'Roboto Mono', monospace",
              fontSize: '11px',
            }}
          >
            {runId}
          </Box>
        </Typography>
      </Box>

      {(totalDemand > 0 || totalSupply > 0) && (
        <List sx={{ padding: 0, margin: '0 0 8px 0' }}>
          <ListItem
            sx={{
              padding: '4px 0',
              fontSize: '12px',
              color: '#4a4a4a',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              component="span"
              sx={{ color: 'primary.main', fontWeight: 'bold', marginRight: '8px' }}
            >
              •
            </Typography>
            Shortage coverage:{' '}
            <Typography component="span" sx={{ fontWeight: 600 }}>
              {totalSupply.toLocaleString()} supplied
            </Typography>
            {' vs '}
            <Typography component="span" sx={{ fontWeight: 600 }}>
              {totalDemand.toLocaleString()} demand
            </Typography>
            {' '}({Math.round(fulfillmentRate * 100)}% fulfilled)
          </ListItem>
          <ListItem
            sx={{
              padding: '4px 0',
              fontSize: '12px',
              color: '#4a4a4a',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              component="span"
              sx={{ color: 'primary.main', fontWeight: 'bold', marginRight: '8px' }}
            >
              •
            </Typography>
            Shortfall remaining:{' '}
            <Typography
              component="span"
              sx={{ fontWeight: 600, color: 'error.main' }}
            >
              {shortfall.toLocaleString()} units
            </Typography>
          </ListItem>
        </List>
      )}

      {(() => {
        let summaryText = executiveSummary;
        let details: string[] = [];

        // Attempt to parse if it looks like JSON
        if (typeof executiveSummary === 'string' && (executiveSummary.trim().startsWith('{') || executiveSummary.trim().startsWith('"{'))) {
          try {
            // Handle double-stringified JSON case if exists
            const toParse = executiveSummary.startsWith('"') ? JSON.parse(executiveSummary) : executiveSummary;
            const parsed = typeof toParse === 'string' ? JSON.parse(toParse) : toParse;

            if (parsed.executive_summary) summaryText = parsed.executive_summary;
            if (parsed.detailed_reasoning) {
              details = Array.isArray(parsed.detailed_reasoning)
                ? parsed.detailed_reasoning
                : [parsed.detailed_reasoning];
            }
          } catch (e) {
            // Fallback to raw text if parse fails
          }
        }

        return (
          <>
            {summaryText && (
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  padding: '10px 12px',
                  borderRadius: '0 6px 6px 0',
                  lineHeight: 1.5,
                  fontSize: '12px',
                  color: '#555',
                  fontStyle: 'italic',
                  marginBottom: '10px',
                }}
              >
                "{summaryText}"
              </Box>
            )}
            {details.length > 0 && (
              <List dense sx={{ padding: 0 }}>
                {details.slice(0, 3).map((item, idx) => (
                  <ListItem key={idx} sx={{ padding: '2px 0', fontSize: '11px', color: '#666' }}>
                    • {item}
                  </ListItem>
                ))}
              </List>
            )}
          </>
        );
      })()}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={handleLoadRun}
          sx={{
            padding: '5px 12px',
            fontSize: '11px',
          }}
        >
          Load this run
        </Button>
        <Typography
          sx={{
            fontSize: '10px',
            color: '#9e9e9e',
          }}
        >
          Stored at {storedAt}
        </Typography>
      </Box>
    </CollapsibleCard>
  );
};

export default LatestRecommendationCard;
